import React, {
  useContext,
  useEffect,
  useRef
} from 'react';
import { flowRight } from 'lodash';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { useLocation } from 'react-router';
import { AppContext } from '../app/AppProvider.component';
import { RoutePath } from '../app/app.constants';
import { getBrand } from '../../pages/ai-chat/ai-chat.utilities';
import { isFeatureFlagEnabled } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import {
  WELCOME_DISCLAIMER_MODAL_ID,
} from '../welcome-disclaimer-modal/welcome-disclaimer-modal.constants';
import { WelcomeDisclaimerConfigMap } from '../welcome-disclaimer-modal/welcome-disclaimer.config';
import {
  fetchAcceptance,
  recordAcceptance,
  shouldShowDisclaimer
} from '../welcome-disclaimer-modal/welcome-disclaimer.utilities';
import WelcomeDisclaimerModal from '../welcome-disclaimer-modal/WelcomeDisclaimerModal.component';

const DISCLAIMER_SUPPRESSED_ROUTES: string[] = [
  RoutePath.APP_LINK_REDIRECT,
  RoutePath.LOGIN,
  RoutePath.LOGOUT,
  RoutePath.TIMEOUT,
  RoutePath.NOT_AUTHORIZED,
  RoutePath.MAINTENANCE,
  RoutePath.UNEXPECTED_ERROR,
  RoutePath.PAGE_NOT_FOUND,
  RoutePath.SUPPORT,
];

const WelcomeDisclaimerRegistrarComponent = (props: ELSPropsFromModalService): JSX.Element => {

  const { appState, setAppState, appStateRef } = useContext(AppContext);
  const location = useLocation();
  const hasEvaluatedThisSessionRef = useRef(false);

  // Trigger the effect on stable primitives only. Depending on the whole
  // appState object would re-run the effect on every state change - including
  // the pendingRequestCount churn caused by the acceptance fetch itself - which
  // previously produced a request storm.
  const { userId } = appState;
  const brand = getBrand(appState);
  const featureFlagsLoaded = Boolean(appState.featureFlagsGrouped);

  useEffect(() => {

    if (hasEvaluatedThisSessionRef.current) {
      return;
    }

    if (DISCLAIMER_SUPPRESSED_ROUTES.includes(location.pathname)) {
      return;
    }

    if (!brand) {
      return;
    }

    if (!featureFlagsLoaded) {
      return;
    }

    const snapshot = appStateRef.current;

    const isEnabled = isFeatureFlagEnabled(
      FEATURE_FLAG.IS_WELCOME_DISCLAIMER_ENABLED,
      snapshot.featureFlagsGrouped,
      snapshot.courseSectionId
    );
    if (!isEnabled) {
      return;
    }

    // Wait for the user to load before committing. Committing on a partial
    // state (e.g. Osmosis, where the brand resolves before the user) would
    // permanently block re-evaluation and the modal would only appear after a
    // manual reload.
    if (!userId) {
      return;
    }

    const config = WelcomeDisclaimerConfigMap[brand];
    const eolsUserId = Number(userId);

    // Commit before the async fetch so re-renders cannot re-fire the request.
    hasEvaluatedThisSessionRef.current = true;

    const onAcknowledge = () => {
      recordAcceptance({
        appState: appStateRef.current,
        setAppState,
        appStateRef,
        userId: eolsUserId,
        brand,
        version: config.version,
      });
    };

    const openModal = () => {
      props.modalService.openModal({
        modalId: WELCOME_DISCLAIMER_MODAL_ID,
        content: (
          <WelcomeDisclaimerModal
            brand={brand}
            config={config}
            onAcknowledge={onAcknowledge}
          />
        ),
      });
    };

    fetchAcceptance({
      appState: appStateRef.current,
      setAppState,
      appStateRef,
      userId: eolsUserId,
      brand,
    })
      .then((stored) => {
        if (!shouldShowDisclaimer(stored, config)) {
          return;
        }
        openModal();
      })
      .catch(() => {
        openModal();
      });
  }, [brand, featureFlagsLoaded, userId, location.pathname, props.modalService, appStateRef, setAppState]);

  return null;
};

const enhance = flowRight(
  ELSWithModalService,
);

const WelcomeDisclaimerRegistrar: React.FC = enhance(WelcomeDisclaimerRegistrarComponent);
export default WelcomeDisclaimerRegistrar;
